import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Popover,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import adminServices from "../../services/adminServices";
import { UserOption } from "../../components/AutoCompleteUserSelect/AutoCompleteUserSelect";

interface BikeDiscountEmployeeSelectProps {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  value: UserOption | null;
  onChange: (selected: UserOption | null) => void;
  error?: string;
  required?: boolean;
  backgroundColor?: string;
  resettable?: boolean;
  disabled?: boolean;
}

const triggerFieldSx = (
  hasError: boolean,
  isInteractionBlocked: boolean,
  backgroundColor: string
) => ({
  cursor: isInteractionBlocked ? "not-allowed" : "pointer",
  borderRadius: "0px",
  height: "40px",
  backgroundColor,
  "& .MuiOutlinedInput-root": {
    borderColor: hasError ? "#d32f2f!important" : "#808080!important",
    borderRadius: "0px",
    height: "40px",
    backgroundColor,
  },
  "& .MuiInputBase-input": {
    color: "#CCCCCC",
    cursor: isInteractionBlocked ? "not-allowed" : "pointer",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#CCCCCC!important",
    opacity: 1,
  },
  "& fieldset": {
    borderColor: hasError ? "#d32f2f !important" : "#808080",
  },
  "&:hover fieldset": {
    borderColor: hasError ? "#d32f2f !important" : "#808080 !important",
  },
});

const BikeDiscountEmployeeSelect: React.FC<BikeDiscountEmployeeSelectProps> = ({
  label = "Employee Name / Code",
  placeholder = "Search employee name or code",
  searchPlaceholder = "Search employee",
  value,
  onChange,
  error,
  required = false,
  backgroundColor = "#393939",
  resettable = true,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEmployees = useCallback(async (term: string) => {
    if (term.length <= 1) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await adminServices.getSearchUser({ searchTerm: term });
      if (data?.statusCode === 200 && data.data.length > 0) {
        const lowerInput = term.toLowerCase().trim();
        const filtered = data.data.filter(
          (item: { fullName: string; userId: string }) =>
            item.fullName.toLowerCase().includes(lowerInput) ||
            item.userId.toLowerCase().includes(lowerInput)
        );

        setOptions(
          filtered.map((user: { userId: string; fullName: string }) => ({
            id: user.userId,
            name: `${user.fullName} | ${user.userId}`,
          }))
        );
      } else {
        setOptions([]);
      }
    } catch (err) {
      console.error("Error fetching employee data:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!anchorEl) {
      setSearchTerm("");
      setOptions([]);
      return;
    }

    const timer = window.setTimeout(() => {
      fetchEmployees(searchTerm);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [anchorEl, searchTerm, fetchEmployees]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
    setOptions([]);
  };

  const handleSelect = (selected: UserOption | null) => {
    onChange(selected);
    handleClose();
  };

  const popoverWidth = anchorEl?.clientWidth || 340;
  const listEmptyMessage =
    searchTerm.length <= 1
      ? "Type at least 2 characters to search"
      : loading
        ? "Searching..."
        : "No results found";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginBottom: error ? "0px" : "8px",
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "medium",
            fontSize: "12px",
            display: "flex",
            color: "#FFFFFF",
          }}
        >
          {label}{" "}
          {required && (
            <span style={{ color: "red", marginLeft: "5px", lineHeight: "1.5" }}>
              *
            </span>
          )}
        </Typography>
      )}

      <TextField
        fullWidth
        variant="outlined"
        value={value?.name ?? ""}
        placeholder={placeholder}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <ExpandMoreIcon sx={{ color: "#CCCCCC" }} />
            </InputAdornment>
          ),
        }}
        sx={triggerFieldSx(!!error, disabled, backgroundColor)}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock
        sx={{ mt: 0.5 }}
      >
        <Box
          sx={{
            width: popoverWidth,
            borderRadius: "0px",
            background: "#212121",
          }}
        >
          <Box p={1} sx={{ background: backgroundColor }}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              sx={{
                borderRadius: "0px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  height: "40px",
                  backgroundColor,
                },
                "& input": {
                  color: "#CCCCCC !important",
                },
                "& input::placeholder": {
                  color: "#CCCCCC",
                  opacity: 1,
                },
                "& fieldset": {
                  borderColor: "#808080",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ gap: 0.5 }}>
                    {loading && (
                      <CircularProgress size={16} sx={{ color: "#CCCCCC" }} />
                    )}
                    <SearchIcon fontSize="small" sx={{ color: "#CCCCCC" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <List
            sx={{
              maxHeight: 220,
              overflowY: "auto",
              p: 1,
              background: backgroundColor,
              m: 0,
            }}
          >
            {resettable && (
              <ListItem
                onClick={() => handleSelect(null)}
                sx={{
                  border: "1px solid #808080",
                  mb: 0.5,
                  bgcolor: backgroundColor,
                  color: "#808080",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#4A4A4A",
                  },
                }}
              >
                <ListItemText
                  primary={placeholder}
                  primaryTypographyProps={{
                    fontSize: "14px",
                    color: "#808080",
                  }}
                />
              </ListItem>
            )}
            {options.length > 0 ? (
              options.map((option) => (
                <ListItem
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  sx={{
                    border: "1px solid #808080",
                    mb: 0.5,
                    bgcolor: backgroundColor,
                    color: "#CCCCCC",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#4A4A4A",
                    },
                    "&:last-child": {
                      mb: 0,
                    },
                  }}
                >
                  <ListItemText
                    primary={option.name}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      color: "#CCCCCC",
                    }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem sx={{ color: "#CCCCCC", justifyContent: "center" }}>
                <ListItemText
                  primary={listEmptyMessage}
                  primaryTypographyProps={{
                    fontSize: "13px",
                    color: "#CCCCCC",
                    textAlign: "center",
                  }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>

      {error && (
        <Typography
          className="f-12"
          sx={{
            fontWeight: "medium",
            textAlign: "left",
            color: "#d32f2f",
            paddingTop: "0px",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default BikeDiscountEmployeeSelect;
